import type { QueryClient } from '@tanstack/react-query'

const ALBUM_PHOTOS_REFRESH_DEBOUNCE_MS = 2000

let refreshTimer: ReturnType<typeof setTimeout> | null = null
const pendingRefreshes = new Map<
  string,
  { queryClient: QueryClient; queryKey: readonly unknown[] }
>()

function serializeQueryKey(queryKey: readonly unknown[]): string {
  return JSON.stringify(queryKey)
}

function runPendingRefresh(job: { queryClient: QueryClient; queryKey: readonly unknown[] }): void {
  void job.queryClient.invalidateQueries({ queryKey: job.queryKey })
}

function flushAllPendingRefreshes(): void {
  refreshTimer = null

  const jobs = [...pendingRefreshes.values()]

  pendingRefreshes.clear()

  for (const job of jobs) {
    runPendingRefresh(job)
  }
}

/** Debounce album photo refetch so rapid placements trigger one server reconciliation per query key. */
export function scheduleAlbumPhotosRefresh(
  queryClient: QueryClient,
  queryKey: readonly unknown[]
): void {
  pendingRefreshes.set(serializeQueryKey(queryKey), { queryClient, queryKey })

  if (refreshTimer != null) {
    clearTimeout(refreshTimer)
  }

  refreshTimer = setTimeout(flushAllPendingRefreshes, ALBUM_PHOTOS_REFRESH_DEBOUNCE_MS)
}

/** Immediately invalidate a pending refresh (e.g. before album panel unmounts). */
export function flushScheduledAlbumPhotosRefresh(queryKey?: readonly unknown[]): void {
  if (queryKey) {
    const job = pendingRefreshes.get(serializeQueryKey(queryKey))

    pendingRefreshes.delete(serializeQueryKey(queryKey))

    if (job) {
      runPendingRefresh(job)
    }

    if (pendingRefreshes.size === 0 && refreshTimer != null) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    return
  }

  if (refreshTimer != null) {
    clearTimeout(refreshTimer)
  }

  flushAllPendingRefreshes()
}

export function cancelScheduledAlbumPhotosRefresh(queryKey?: readonly unknown[]): void {
  if (queryKey) {
    pendingRefreshes.delete(serializeQueryKey(queryKey))

    if (pendingRefreshes.size === 0 && refreshTimer != null) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    return
  }

  if (refreshTimer != null) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  pendingRefreshes.clear()
}

/** Fired whenever a photo's placed-on-spread status may have changed — either direction, a photo
 * newly placed or one just removed — so the Photos tray knows to refresh its server-cached
 * `placedFlag` data (see the listener in
 * `right-panel/photos-panel/hooks/use-album-photos-placement-refresh.ts`). */
export const PHOTO_PLACEMENT_CHANGED_ON_SPREAD_EVENT = 'photo-placement-changed-on-spread'

export function notifyPhotoPlacementChangedOnSpread(): void {
  window.dispatchEvent(new CustomEvent(PHOTO_PLACEMENT_CHANGED_ON_SPREAD_EVENT))
}
